/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import {
  ICarbonScopeOneData,
  IDetailComponentProps
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { Card } from '@scuf/common';
import PortfolioCarbonScopeOneSavingsBySiteDetailComponent from '../PortfolioCarbonScopeOneSavingsBySiteWidget/PortfolioCarbonScopeOneSavingsBySiteDetailComponent';

interface IPortfolioCarbonScopeTwoSavingsBySiteComponentContent {
  data: ICarbonScopeOneData;
  UtilityBillType: string;
  onRefresh: () => Promise<void>;
}

export const PortfolioCarbonSavingsBySiteComponentContent = (
  props: IPortfolioCarbonScopeTwoSavingsBySiteComponentContent
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, UtilityBillType } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  return (
    <div>
      <PortfolioCarbonScopeOneSavingsBySiteDetailComponent
        data={data}
        UtilityBillType={UtilityBillType}
      />
    </div>
  );
};

const PortfolioCarbonScopeTwoSavingsBySiteComponentWithLoader = withLoadingStatus(
  withErrorStatus(PortfolioCarbonSavingsBySiteComponentContent)
);

const PortfolioCarbonScopeTwoSavingsBySiteComponent = (
  props: IDetailComponentProps
) => {
  const { dataConnector, UtilityBillType } = props;

  const [t] = useTranslation();
  const d = descriptors.energyManagement;

  const { all, loading, error, status } = dataConnector.data;
  const carbonScopeTwoSavingsData = (all as unknown) as ICarbonScopeOneData;
  const isDataAvailable = carbonScopeTwoSavingsData ? true : false;
  const [data, setData] = useState({} as ICarbonScopeOneData);
  const [apiStatusCode, setStatusCode] = useState<number | null>(0);
  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [errorInfo, setErrorInfo] = useState(error);

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(carbonScopeTwoSavingsData);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);
  return (
    <Card className="Savemeter">
      <Card.Header className="emaas-carbon-view-details-total-emissions-savings-meter-card-header">
        <div className="emaas-site-count-header">
          <div>
            {t(d.breakdownCarbonSites, {
              count: data.breakdownBySite?.length || 0
            })}
          </div>
          <div>{t(d.SITEWITHDATA)}</div>
        </div>
      </Card.Header>
      <Card.Content>
        <div className="meter-savings-Datatable detail-view">
          <PortfolioCarbonScopeTwoSavingsBySiteComponentWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={apiStatusCode}
            onRefresh={onRefresh}
            data={data}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={t(d.NODATAAVAILABLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            UtilityBillType={UtilityBillType || ''}
          />
        </div>
      </Card.Content>
    </Card>
  );
};
export default PortfolioCarbonScopeTwoSavingsBySiteComponent;
